import { MeshBasicMaterialProps } from '@react-three/fiber'
import { useMemo } from 'react'
import { BufferGeometry, DoubleSide, Vector3 } from 'three'
import { Earcut } from 'three/src/extras/Earcut.js'
import { useRoofMeshes } from '~/components/DrawingCanvas/Roof/hooks/useRoofMeshes'
import { handleClick } from '~/components/DrawingCanvas/util/eventHandlers'
import { getRenderOrder } from '~/components/DrawingCanvas/util/renderOrder'
import { modelToUI, UNIT_CONVERSION_SCALAR } from '~/components/DrawingCanvas/util/units'
import LayerKeys from '~/config/layerKeys'
import ObjectClassName from '~/config/objectClassNames'
import theme from '~/config/theme'
import { useAppSelector, useAppDispatch } from '~/store/hooks'
import { RoofSection } from '~/store/objects/types'
import { selectObjects } from '~/store/selectedObjects'

const Z_FIGHT_OFFSET = -0.001
const getRoofSectionMaterialProps = (isSelected: boolean): MeshBasicMaterialProps => ({
  color: theme.colors.three.objects.roofSection[isSelected ? 'selected' : 'default'],
  transparent: true,
  side: DoubleSide,
  opacity: 0.5,
  depthWrite: false,
})

export const SecondaryStructure = () => {
  const isVisible = useAppSelector(state => state.layers.layers.SECONDARY_MOUNTING_STRUCTURE.visible)
  const roofSections = useAppSelector(state => state.objects.present.roofSections)
  const dispatch = useAppDispatch()

  return (
    <>
      {Object.values(roofSections).map(section => {
        const { id, secondaryStructureDepth, secondaryStructureStyle } = section
        const selectedSection = roofSections[id]
        return (
          <group
            key={section.id}
            renderOrder={getRenderOrder(LayerKeys.ROOF_SECTIONS)}
            visible={isVisible}
            name={LayerKeys.ROOF_SECTIONS}
            onClick={
              selectedSection &&
              handleClick(() =>
                dispatch(
                  selectObjects({ objects: [{ className: ObjectClassName.ROOF_SECTION, id: selectedSection.id }] })
                )
              )
            }
            scale={1 / UNIT_CONVERSION_SCALAR}
            position-z={-modelToUI(secondaryStructureDepth) + Z_FIGHT_OFFSET}
          >
            {secondaryStructureStyle === 'flat' ? <Flat {...section} /> : <RoofConforming {...section} />}
          </group>
        )
      })}
    </>
  )
}

const Flat = (section: RoofSection) => {
  const roofs = useAppSelector(state => state.objects.present.roofs)
  const isSelected = useAppSelector(state => !!state.selectedObjects.find(({ id }) => id === section.id))
  const geometry = useMemo(() => {
    const height = roofs[section.roofId]!.height
    const perimeterPoints = section.perimeterPoints.map(([x, y]) => new Vector3(x, y, height))
    perimeterPoints.pop()
    const triangleIndices = Earcut.triangulate(perimeterPoints.map(v => v.toArray()).flat(), undefined, 3)
    const geometry = new BufferGeometry().setFromPoints(perimeterPoints)
    geometry.setIndex(triangleIndices)
    geometry.computeVertexNormals()
    return geometry
  }, [section, roofs])

  return (
    <mesh geometry={geometry}>
      <meshBasicMaterial {...getRoofSectionMaterialProps(isSelected)} />
    </mesh>
  )
}

const RoofConforming = (section: RoofSection) => {
  const roofMeshes = useRoofMeshes()
  const roofMesh = roofMeshes.find(([id]) => id === section.roofId)?.[1]
  const isSelected = useAppSelector(state => !!state.selectedObjects.find(({ id }) => id === section.id))
  if (!roofMesh) return <></>
  return (
    <primitive object={roofMesh.clone()}>
      <meshBasicMaterial {...getRoofSectionMaterialProps(isSelected)} />
    </primitive>
  )
}
